import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import StarRating from '../../../components/StarRating';
import TrustBadge from '../../../components/TrustBadge';
import SellerRatingsPanel from '../../marketplace/components/SellerRatingsPanel';
import ratingsService from '../../../services/ratingsService';

const UserTrustScore = ({ userId, userName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPanel, setShowPanel] = useState(false);

  useEffect(() => {
    if (userId) loadData();
  }, [userId]);

  const loadData = async () => {
    setLoading(true);
    const { data: result } = await ratingsService?.getUserReputationWithBadges(userId);
    setData(result);
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 animate-pulse">
        <div className="h-4 bg-muted/30 rounded w-32 mb-4" />
        <div className="grid grid-cols-2 gap-4">
          <div className="h-16 bg-muted/20 rounded-lg" />
          <div className="h-16 bg-muted/20 rounded-lg" />
        </div>
      </div>
    );
  }

  const rep = data?.reputation;
  const badges = data?.badges || [];
  const reviewCount = (data?.reviews?.length || 0);
  const hasActivity = (rep?.total_sales > 0 || rep?.total_purchases > 0);

  return (
    <>
      <div className="bg-card border border-border rounded-xl p-6 premium-hover">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center space-x-2">
            <Icon name="Shield" size={18} className="text-primary" />
            <h3 className="text-base font-semibold text-foreground">Trust & Reputation</h3>
          </div>
          {reviewCount > 0 && (
            <button
              onClick={() => setShowPanel(true)}
              className="text-xs text-primary hover:text-primary/80 transition-colors flex items-center space-x-1"
            >
              <span>View all {reviewCount} reviews</span>
              <Icon name="ChevronRight" size={14} />
            </button>
          )}
        </div>

        {/* Rating Scores */}
        <div className="grid grid-cols-2 gap-4 mb-5">
          {/* Seller Rating */}
          <div className="bg-muted/10 border border-border rounded-lg p-4 text-center">
            <div className="flex items-center justify-center space-x-1 mb-1">
              <Icon name="Tag" size={14} className="text-primary" />
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Seller</p>
            </div>
            <div className="text-3xl font-bold text-foreground mb-1">
              {rep?.seller_rating > 0 ? rep?.seller_rating?.toFixed(1) : '—'}
            </div>
            <StarRating rating={rep?.seller_rating || 0} size={14} showValue={false} />
            <p className="text-xs text-muted-foreground mt-1">{rep?.total_sales || 0} sales</p>
          </div>

          {/* Buyer Rating */}
          <div className="bg-muted/10 border border-border rounded-lg p-4 text-center">
            <div className="flex items-center justify-center space-x-1 mb-1">
              <Icon name="ShoppingBag" size={14} className="text-success" />
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Buyer</p>
            </div>
            <div className="text-3xl font-bold text-foreground mb-1">
              {rep?.buyer_rating > 0 ? rep?.buyer_rating?.toFixed(1) : '—'}
            </div>
            <StarRating rating={rep?.buyer_rating || 0} size={14} showValue={false} />
            <p className="text-xs text-muted-foreground mt-1">{rep?.total_purchases || 0} purchases</p>
          </div>
        </div>

        {/* Trust Badges */}
        {badges?.length > 0 ? (
          <div>
            <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wide">Trust Badges</p>
            <div className="flex flex-wrap gap-2">
              {badges?.slice(0, 6)?.map(badge => (
                <TrustBadge key={badge?.id} badgeType={badge?.badge_type} size="sm" />
              ))}
              {badges?.length > 6 && (
                <button
                  onClick={() => setShowPanel(true)}
                  className="px-2 py-1 text-xs text-muted-foreground bg-muted/20 rounded-full hover:text-foreground transition-colors"
                >
                  +{badges?.length - 6} more
                </button>
              )}
            </div>
          </div>
        ) : !hasActivity ? (
          <div className="text-center py-3">
            <Icon name="Star" size={24} className="mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-xs text-muted-foreground">No transactions yet — badges earned after first trade</p>
          </div>
        ) : null}

        {/* View Reviews CTA */}
        {reviewCount > 0 && (
          <button
            onClick={() => setShowPanel(true)}
            className="w-full mt-4 py-2 text-sm text-primary border border-primary/20 rounded-lg hover:bg-primary/5 transition-colors flex items-center justify-center space-x-2"
          >
            <Icon name="MessageSquare" size={14} />
            <span>Read Reviews</span>
          </button>
        )}
      </div>

      {showPanel && (
        <SellerRatingsPanel
          userId={userId}
          userName={userName}
          onClose={() => setShowPanel(false)}
        />
      )}
    </>
  );
};

export default UserTrustScore;
